import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, Moon, Search, Sun, X } from "lucide-react";
import { navItems, type NavItem } from "../data/navigation";
import { educationTopics } from "../data/educationCatalog";
import { useAuth } from "../auth";
import { Icon } from "./Icon";

function NavBranch({ item, depth = 0, close }: { item: NavItem; depth?: number; close: () => void }) {
  const location = useLocation();
  const activeChild = item.children?.some((child) => child.href === location.pathname || child.children?.some((nested) => nested.href === location.pathname));
  const [open, setOpen] = useState(Boolean(item.expanded || activeChild));
  if (item.children?.length) {
    return <div className="nav-branch">
      <button className={`nav-item depth-${depth}`} onClick={() => setOpen(!open)} aria-expanded={open}>
        <Icon name={item.icon} /><span>{item.label}</span><span className="chevron">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="nav-children">{item.children.map((child) => <NavBranch key={`${child.label}-${child.href ?? "group"}`} item={child} depth={depth + 1} close={close} />)}</div>}
    </div>;
  }
  return <NavLink to={item.href ?? "/"} onClick={close} className={({ isActive }) => `nav-item depth-${depth}${isActive ? " active" : ""}`}>
    <Icon name={item.icon} /><span>{item.label}</span>
  </NavLink>;
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dark, setDark] = useState(() => localStorage.getItem("bp-theme") === "dark" || (!localStorage.getItem("bp-theme") && matchMedia("(prefers-color-scheme: dark)").matches));

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
    localStorage.setItem("bp-theme", dark ? "dark" : "light");
  }, [dark]);

  const matches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("it");
    if (query.length < 2) return [];
    return educationTopics.filter((topic) => `${topic.title} ${topic.area} ${topic.subject}`.toLocaleLowerCase("it").includes(query)).slice(0, 8);
  }, [search]);

  const close = () => setMobileOpen(false);
  return <div className="app-shell">
    <aside className={`sidebar${mobileOpen ? " open" : ""}`}>
      <div className="brand-row">
        <Link to="/" className="brand" onClick={close}><img src="/favicon/android-chrome-192x192.png" className="brand-logo" alt="BrainPower" /><span>BrainPower</span></Link>
        <button className="icon-button mobile-only" onClick={close} aria-label="Chiudi menu"><X /></button>
      </div>
      <nav className="main-nav" aria-label="Navigazione principale">
        {navItems.filter((item) => item.label !== "Signout" && (item.label !== "Admin" || user)).map((item) => <NavBranch key={item.label} item={item} close={close} />)}
        <button className="nav-item signout-item" onClick={logout}><Icon name="logout" /><span>Signout</span></button>
      </nav>
    </aside>
    {mobileOpen && <button className="scrim" onClick={close} aria-label="Chiudi menu" />}
    <section className="main-column">
      <header className="topbar">
        <button className="icon-button mobile-only" onClick={() => setMobileOpen(true)} aria-label="Apri menu"><Menu /></button>
        <div className="global-search">
          <Search size={18} />
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cerca tra materie e lezioni" aria-label="Cerca" />
          {matches.length > 0 && <div className="search-results">{matches.map((topic) => <button key={topic.href} onClick={() => { navigate(topic.href); setSearch(""); }}><span>{topic.title}</span><small>{topic.area}</small></button>)}</div>}
        </div>
        <button className="icon-button" onClick={() => setDark(!dark)} aria-label={dark ? "Tema chiaro" : "Tema scuro"}>{dark ? <Sun /> : <Moon />}</button>
        <div className="topbar-divider" />
        <div className="topbar-user">
          <div className="avatar">{(user?.firstName?.[0] ?? user?.email?.[0] ?? "U").toUpperCase()}</div>
          <div><small>Benvenuto</small><strong>{[user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email}</strong></div>
        </div>
      </header>
      <main className="app-main">{children}</main>
      <footer><span>BrainPower</span><span>Impara, prova, migliora.</span></footer>
    </section>
  </div>;
}
